import type { Conversation, LastMessage } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import type { Channel } from "pusher-js";
import { useCallback, useEffect, useRef } from "react";
import { usePublicConfig } from "@/api/queries/config";
import { mapProfile, type RawConversationProfile } from "@/api/queries/messenger";
import { useAuth } from "@/hooks/use-auth";
import { disconnectPusher, getPusherClient } from "@/lib/pusher";
import { queryKeys } from "@/lib/query-keys";
import { useMessengerStore } from "@/stores/messenger-store";
import { useToastStore } from "@/stores/toast-store";

interface PusherUpdatePayload {
	profile: RawConversationProfile;
	conversation_id: number;
	last_msg: LastMessage;
}

export function usePusher() {
	const { user } = useAuth();
	const { data: config } = usePublicConfig();
	const queryClient = useQueryClient();
	const channelRef = useRef<Channel | null>(null);
	const { setPusherConnected, setPusherChannelName } = useMessengerStore();

	const handleUpdate = useCallback(
		(rawData: PusherUpdatePayload | string) => {
			// Pusher kann data als JSON-String oder bereits geparstes Objekt liefern
			let data: PusherUpdatePayload;
			try {
				data = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
			} catch {
				console.error("[Pusher] Ungültiges Event-Payload:", rawData);
				return;
			}

			// Update unread count
			queryClient.invalidateQueries({
				queryKey: queryKeys.messenger.unreadCount(),
			});

			const mappedProfile = mapProfile(data.profile);

			// Update conversations list optimistically — Cache hält die InfiniteQuery-Shape
			// {pages: [{conversations, pagination}], pageParams: [...]} seit der Pagination-Umstellung.
			let foundInPages = false;
			queryClient.setQueryData<{
				pages: { conversations: Conversation[]; pagination?: unknown }[];
				pageParams: unknown[];
			}>(queryKeys.messenger.conversations(), (old) => {
				if (!old) return old;
				const updatedPages = old.pages.map((page) => {
					const idx = page.conversations.findIndex((c) => c.id === data.conversation_id);
					if (idx < 0) return page;
					foundInPages = true;
					const updatedConversations = [...page.conversations];
					updatedConversations[idx] = {
						...updatedConversations[idx],
						last_msg: data.last_msg,
						unread_count: updatedConversations[idx].unread_count + 1,
						profile: updatedConversations[idx].profile ?? mappedProfile,
					};
					return { ...page, conversations: updatedConversations };
				});
				return foundInPages ? { ...old, pages: updatedPages } : old;
			});

			if (!foundInPages) {
				// Neue Conversation oder noch nicht paginiert — frischer Fetch
				queryClient.invalidateQueries({
					queryKey: queryKeys.messenger.conversations(),
				});
			}

			// ChatView-Header lebt auf der separaten conversationInfo-Query — auch dort aktualisieren
			queryClient.setQueryData<Conversation>(
				queryKeys.messenger.conversationInfo(String(data.conversation_id)),
				(old) =>
					old
						? {
								...old,
								last_msg: data.last_msg,
								unread_count: old.unread_count + 1,
								profile: old.profile ?? mappedProfile,
							}
						: old,
			);

			// Invalidate specific conversation messages
			queryClient.invalidateQueries({
				queryKey: queryKeys.messenger.conversation(String(data.conversation_id)),
			});

			// Toast-Benachrichtigung fuer eingehende Nachricht
			const senderName = data.profile?.name ?? "Jemand";
			const preview =
				data.last_msg?.type === "text" && data.last_msg.message
					? data.last_msg.message.length > 60
						? `${data.last_msg.message.substring(0, 60)}...`
						: data.last_msg.message
					: data.last_msg?.type === "kiss"
						? "hat dir einen Kuss gesendet"
						: data.last_msg?.type === "gift"
							? "hat dir ein Geschenk gesendet"
							: data.last_msg?.type === "image"
								? "hat dir ein Bild gesendet"
								: "Neue Nachricht";

			// Profilbild-URL: kann String (Pusher) oder Objekt (Search-API) sein
			const imageUrl =
				typeof data.profile?.image === "string"
					? data.profile.image
					: (data.profile?.image?.thumb ?? undefined);

			useToastStore.getState().addToast({
				type: "info",
				title: `Neue Nachricht von ${senderName}`,
				message: preview,
				image: imageUrl,
				link: `/messenger/${data.conversation_id}`,
				duration: 8000,
			});
		},
		[queryClient],
	);

	// Channel-Auth basiert auf der Customer-ID (siehe routes/channels.php).
	// Beams ist ein separates System für Push-Notifications und nicht relevant für den Channel-Namen.
	const pusherKey = config?.pusherKey;
	const pusherCluster = config?.pusherCluster ?? "eu";
	const channelName = user?.id ? `private-chat.${user.id}` : null;

	useEffect(() => {
		if (!pusherKey || !channelName) {
			return;
		}

		const pusher = getPusherClient(pusherKey, pusherCluster);
		const channel = pusher.subscribe(channelName);
		channelRef.current = channel;
		setPusherChannelName(channelName);

		channel.bind("pusher:subscription_succeeded", () => {
			setPusherConnected(true);
		});

		channel.bind("Update", handleUpdate);

		return () => {
			channel.unbind("Update", handleUpdate);
			pusher.unsubscribe(channelName);
			channelRef.current = null;
			setPusherConnected(false);
			setPusherChannelName(null);
		};
	}, [
		pusherKey,
		pusherCluster,
		channelName,
		handleUpdate,
		setPusherConnected,
		setPusherChannelName,
	]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			disconnectPusher();
		};
	}, []);
}
